import React, { useState, useContext } from 'react';
import { Typography, Button, Tooltip, Box, CircularProgress } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { ReCaptchaContext } from '../reusable-components/recatcha-provider';
import { AwardCard as StyledAwardCard, AwardIcon, AwardYear, YearBadge, FrequencyBadge } from '../styles/awards.styles';
import _ from 'lodash';
import { logCustomEvent, remoteConfig } from '../utils/firebase';
import { generateVisualMetaphor } from '../utils/imageService';
import { getValue } from 'firebase/remote-config';

const iconMap = {
    EmojiEvents: EmojiEventsIcon,
    Star: StarIcon,
    WorkspacePremium: WorkspacePremiumIcon,
    MilitaryTech: MilitaryTechIcon
};

const MAX_DESCRIPTION_LENGTH = 150;

const AwardCard = ({ award, onReadMore, onViewCertificate }) => {
    const [loading, setLoading] = useState(false);
    const { captchaVerified, onCaptchaVerify } = useContext(ReCaptchaContext);
    const [metaphorImage, setMetaphorImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState("");
    const isVisualMetaphorEnabled = getValue(remoteConfig, 'visualMetaphorEnabled').asBoolean();

    const getIcon = (iconName) => {
        const IconComponent = iconMap[iconName];
        return IconComponent ? <IconComponent /> : <EmojiEventsIcon />;
    };

    const truncateDescription = (text) => {
        if (text.length <= MAX_DESCRIPTION_LENGTH) return text;
        return text.substring(0, MAX_DESCRIPTION_LENGTH) + '...';
    };

    const handleViewCertificate = async () => {
        logCustomEvent('view_award_certificate', {
            award_title: award.title
        });
        if (captchaVerified) {
            onViewCertificate(award);
            return;
        }

        if (process.env.NODE_ENV === 'production') {
            setLoading(true);
            const status = await onCaptchaVerify();
            if (status) {
                setLoading(false);
                onViewCertificate(award);
            }
        } else {
            onViewCertificate(award);
        }
    };

    const handleGenerateImage = async () => {
        setImageLoading(true);
        setImageError("");
        setMetaphorImage(null);
        try {
            const imgUrl = await generateVisualMetaphor(award.description);
            setMetaphorImage(imgUrl);
        } catch (err) {
            setImageError("Failed to generate image.");
        }
        setImageLoading(false);
    };

    return (
        <StyledAwardCard
            custom={award.index}
            variants={award.cardVariants}
            initial="hidden"
            animate="visible"
            whileHover="hover"
        >
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                width: '100%',
                mb: 2
            }}>
                <AwardIcon>
                    {getIcon(award.icon)}
                </AwardIcon>
                {award.certificateUrl && (
                    <Button
                        onClick={handleViewCertificate}
                        startIcon={!loading && <AssignmentTurnedInIcon sx={{ fontSize: '1rem' }} />}
                        disabled={loading}
                        color="secondary"
                        sx={{
                            borderRadius: '20px',
                            textTransform: 'none',
                            padding: '4px 12px',
                            fontSize: '0.875rem',
                            minHeight: 0,
                            minWidth: 0,
                            position: 'relative',
                        }}
                    >
                        {!loading ? 'Certificate' : <CircularProgress size={16} color='secondary' sx={{ position: 'absolute', left: '50%', marginLeft: '-8px' }} />}
                    </Button>
                )}
            </Box>
            <Typography
                variant="h5"
                gutterBottom
                sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2
                }}
            >
                {award.title}
            </Typography>
            <Typography
                variant="subtitle1"
                sx={{
                    color: 'text.secondary',
                    mb: 2,
                    fontWeight: 500
                }}
            >
                {award.organization}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    mb: 2
                }}
            >
                {truncateDescription(award.description)}
                {award.description.length > MAX_DESCRIPTION_LENGTH && (
                    <Button
                        onClick={() => onReadMore(award)}
                        sx={{
                            ml: 1,
                            color: 'primary.main',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        Read More
                    </Button>
                )}
            </Typography>
            {isVisualMetaphorEnabled && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                {!metaphorImage && !imageLoading && (
                    <Button variant="outlined" onClick={handleGenerateImage}>
                        Generate Visual Metaphor
                    </Button>
                )}
                {imageLoading && <CircularProgress size={32} />}
                {imageError && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>{imageError}</Typography>
                )}
                {metaphorImage && (
                    <>
                        <img src={metaphorImage} alt="Visual Metaphor" style={{ maxWidth: '100%', borderRadius: 8 }} />
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            AI-generated visual metaphor
                        </Typography>
                    </>
                )}
                </Box>
            )}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 2
            }}>
                <AwardYear>
                    <YearBadge component="span">
                        {award.year}
                    </YearBadge>
                    {!_.isNil(award.times) && (
                        <Tooltip 
                            title={`This award was received ${award.times} times in ${award.year}`}
                            arrow
                            placement="top"
                        >
                            <FrequencyBadge component="span">
                                ×{award.times}
                            </FrequencyBadge>
                        </Tooltip>
                    )}
                </AwardYear>
            </Box>
        </StyledAwardCard>
    );
};

export default AwardCard; 